import { PencilSimpleIcon, PlusIcon } from '@phosphor-icons/react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search } from 'app/components/Search'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { PageScroll } from 'app/components/layout/PageScroll'
import { LaneCount } from 'app/components/lists/board/LaneCount'
import { LaneLoadMore } from 'app/components/lists/board/LaneLoadMore'
import { DefaultStageMark } from 'app/components/lists/board/DefaultStageMark'
import { ListBoard } from 'app/components/lists/board/ListBoard'
import { ListBoardHeader } from 'app/components/lists/board/ListBoardHeader'
import type { ListBoardLaneDef } from 'app/components/lists/board/boardTypes'
import { ListSlotCard } from 'app/components/lists/board/ListSlotCard'
import { ListKindLine } from 'app/components/lists/ListKindLine'
import { PersonalListMarker } from 'app/components/lists/PersonalListMarker'
import { useListBoard } from 'app/components/lists/board/useListBoard'
import { useListStageModals } from 'app/components/lists/board/useListStageModals'
import { useStageVisibility } from 'app/components/lists/manual/useStageVisibility'
import { AddContactsModal } from 'app/components/pipelines/AddContactsModal'
import { PipelineLocationSelector } from 'app/components/pipelines/PipelineLocationSelector'
import { contactLocationClause, resolveContactLocationFilter } from 'app/components/pipelines-page/locationFilter'
import { Button } from 'app/components/ui/button'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { useListTraversal } from 'app/hooks/useListTraversal'
import { useLocationFilter } from 'app/hooks/useLocationFilter'
import { usePipelineCommContext } from 'app/hooks/usePipelineCommContext'
import { usePipelineTaskCounts } from 'app/hooks/usePipelineTaskCounts'
import { useSettings } from 'app/hooks/useSettings'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { showModal } from 'app/lib/modal'
import { cn } from 'app/lib/utils'
import { notifyError } from 'app/services/notify.client'
import type { List } from 'app/generated/graphql'
import type { ContactJson } from 'app/types/entities'
import { useHandleSearchParams } from 'app/utils'
import { bg } from 'lib/background'
import { contactNameTerms } from 'lib/contactSearch'
import { logError } from 'lib/logging'
import { NO_STAGE, type LaneState } from 'lib/manualListLanes'
import { defaultStageIdOf } from 'lib/practiceLists'
import { getListCardConfig, LIST_CARD_CONFIG_SETTING, type ListCardConfig } from 'lib/listCardConfig'
import { isFieldEligible } from 'lib/listCardFields'
import { useSidebarCounts } from 'app/hooks/useSidebarCounts'
import { isPersonalList } from 'lib/listVisibility'

const MODAL_OPTIONS = { isFullScreen: false, removePadding: true, useRadix: true } as const

const EMPTY_LANE: LaneState<ContactJson> = { items: [], total: 0, hasMore: false, page: 0, pendingIds: [] }

export function ManualListBoard({
  list,
  viewToggle,
  titleActions,
  onStagesChanged,
}: {
  list: List
  viewToggle?: ReactNode
  titleActions?: ReactNode
  onStagesChanged: () => void
}) {
  const { counts } = useSidebarCounts()
  // Filters: the old page's own recipe, so a location filter can never "fail open".
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const { locationIds, locationOptions, isLoading } = useLocationFilter()
  const locationFilter = useMemo(
    () => resolveContactLocationFilter({ locationIds, locationOptions, isLoading }),
    [locationIds, locationOptions, isLoading],
  )
  const query = searchParams.get(`query`) ?? ``
  const filterTerms = useMemo(
    () => [contactLocationClause(locationFilter), ...contactNameTerms(query)].filter(Boolean),
    [locationFilter, query],
  )

  // Per-user stage visibility and order, shared with the settings dialog that edits it.
  const { visibleStages } = useStageVisibility(list)

  // Every stage, not just the visible ones: hiding a stage must not stop its lane from being read.
  // Only a list with no stages draws a "No stage" lane (see `useListBoard`), so a deleted stage's
  // members never show up stranded here: they are not on a lane at all until they are moved.
  const stageIds = useMemo(() => (list.stages ?? []).map((stage) => stage.id), [list.stages])
  const board = useListBoard({
    listId: list.id,
    stageIds,
    filterTerms,
    ready: locationFilter.state !== `pending`,
  })

  const onStagesChangedThenReload = () => {
    onStagesChanged()
    board.reload()
  }
  const { openAddStage, openStageEditor } = useListStageModals({ list, onChanged: onStagesChangedThenReload })

  // The card's needs. The settings dialog writes this key; the store keeps both readers in step.
  const [rawCardConfig] = useSettings<ListCardConfig>(LIST_CARD_CONFIG_SETTING)
  const cardConfig = getListCardConfig(rawCardConfig, isFieldEligible)

  // The drawer. A lane's row is a list row, so opening a card re-reads the full record. A
  // request id guards a slow read landing after the user opened a different card, and the
  // mounted ref keeps an even later read from setting state on an unmounted board.
  const [drawerContact, setDrawerContact] = useState<ContactJson | undefined>(undefined)
  const openRequestId = useRef(0)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const openContact = async (contactId: string) => {
    if (drawerContact?.id === contactId) return
    const requestId = ++openRequestId.current
    const contact = await gqlFetchOne(`contact`, contactId)
    if (!mounted.current || requestId !== openRequestId.current) return
    if (!contact) {
      logError(`Error opening a contact from a manual list's board`, new Error(`Could not read contact ${contactId}`))
      notifyError(`Couldn't open that contact. Please try again.`)
      return
    }
    setDrawerContact(contact)
  }
  const closeDrawer = () => {
    setDrawerContact(undefined)
    board.reload()
    handleSearchParams({ drawer: undefined })
  }

  // A link into this list carries `?drawer=<id>`. Keyed on the id itself, not
  // `searchParams`, so this fires exactly once per incoming id; `openContact`
  // already no-ops when that id is the one already open.
  const drawerParam = searchParams.get(`drawer`)
  useEffect(() => {
    if (drawerParam && drawerParam !== `new`) bg(openContact(drawerParam))
  }, [drawerParam])

  const openAddContacts = () =>
    showModal(
      <AddContactsModal
        pipeline={list}
        defaultStageId={defaultStageIdOf(list)}
        // A person already on the list is harmless to add again, so nobody is left out of the search.
        excludeSearch=""
        onAdd={(added, stage) => {
          if (stage.id) bg(board.add(added, stage.id))
        }}
      />,
      { ...MODAL_OPTIONS, title: `Add contacts` },
    )

  // Lanes.
  const defaultStageId = defaultStageIdOf(list)
  const noStageLane = board.lanes[NO_STAGE]
  const noStageFooter = noStageLane?.hasMore && (
    <LaneLoadMore
      remaining={noStageLane.total - noStageLane.items.length}
      loading={board.loadingLanes.has(NO_STAGE)}
      onClick={() => board.loadMore(NO_STAGE)}
    />
  )
  const lanes: ListBoardLaneDef<ContactJson, string>[] = [
    // Only a list with no stages has one: every member of a list with stages sits in a stage's own
    // lane. Drawn either with somebody in it, or when its own read failed (nothing may be there,
    // but the user still needs to know it wasn't checked).
    ...((list.stages?.length ?? 0) === 0 && (noStageLane?.items.length || board.failedLanes.has(NO_STAGE))
      ? [
          {
            key: NO_STAGE,
            label: `No stage`,
            count: <LaneCount shown={noStageLane?.items.length ?? 0} total={noStageLane?.total ?? 0} />,
            headerExtra: (
              <p className="mt-0.5 text-body-xs leading-relaxed text-subtle">Add a stage to start working them.</p>
            ),
            items: noStageLane?.items ?? [],
            droppable: false,
            footer: noStageFooter,
            emptyLabel: board.failedLanes.has(NO_STAGE) ? `Couldn't load this stage` : undefined,
          },
        ]
      : []),
    ...visibleStages.map((stage) => {
      const rawLane = board.lanes[stage.id!]
      const lane = rawLane ?? EMPTY_LANE
      return {
        key: stage.id!,
        label: stage.name,
        labelAdornment: (
          <span
            className={cn(`h-2 w-2 shrink-0 rounded-full`, stage.color ? `bg-${stage.color}` : `bg-surface-strong`)}
          />
        ),
        count: <LaneCount shown={lane.items.length} total={lane.total} />,
        headerAction: (
          <>
            {/* Where new entries, and anyone without a stage yet, land (see `defaultStageIdOf`). */}
            {stage.id === defaultStageId && <DefaultStageMark />}
            <Button
              variant="icon"
              size="icon-sm"
              aria-label={`Edit ${stage.name}`}
              onClick={() => openStageEditor(stage)}
            >
              <PencilSimpleIcon size={14} weight="bold" />
            </Button>
          </>
        ),
        items: lane.items,
        // A lane whose own read failed says so, instead of "Loading" forever: no entry ever
        // lands in `board.lanes` for it, since a failed read never reaches `setLanes`. Checked
        // before the "never read yet" case below, which would otherwise also be true for it.
        // No entry in `board.lanes` yet means this stage has never been read, not that it's
        // empty: only a lane that exists and has settled says "Nothing here".
        emptyLabel: board.failedLanes.has(stage.id!)
          ? `Couldn't load this stage`
          : !rawLane || board.loading
            ? `Loading`
            : `Nothing here`,
        footer: lane.hasMore && (
          <LaneLoadMore
            remaining={lane.total - lane.items.length}
            loading={board.loadingLanes.has(stage.id!)}
            onClick={() => board.loadMore(stage.id!)}
          />
        ),
      }
    }),
  ]

  // The board's own reading list, in lane order. Built from the DRAWN lanes, not
  // `board.lanes`: that now carries an entry for every stage, visible or hidden (see above),
  // and a hidden stage's people are never on screen for a card's task counts, comm context or
  // the drawer's next/previous to reach.
  const drawnContacts = lanes.flatMap((lane) => lane.items)
  const contactIds = drawnContacts.map((contact) => contact.id!).filter(Boolean)
  const taskCounts = usePipelineTaskCounts(contactIds, cardConfig.slots.meta.includes(`tasks`))
  const commContext = usePipelineCommContext(drawnContacts, cardConfig.showCommunications)
  // The old board's next/previous, over the same list this board actually draws.
  const traversal = useListTraversal(drawnContacts, drawerContact, (item) => {
    if (item.id) bg(openContact(item.id))
  })

  return (
    <PageScroll contentClassName="flex h-full flex-col pb-6 pt-8">
      <ListBoardHeader
        title={list.name || `Untitled list`}
        titleMarker={<PersonalListMarker personal={isPersonalList(list)} />}
        subheader={<ListKindLine kind="manual" />}
        totalItems={counts.lists?.[list.id]}
        actions={
          <>
            {titleActions}
            <Button onClick={openAddContacts} disabled={(list.stages?.length ?? 0) === 0}>
              <PlusIcon size={16} weight="bold" /> Add contacts
            </Button>
          </>
        }
        headerControls={viewToggle}
        controls={
          <>
            <Search />
            <PipelineLocationSelector />
          </>
        }
        banner={
          board.error && (
            <p className="mt-4 rounded-lg bg-surface-subtle px-3 py-2 text-body-xs leading-relaxed text-strong">
              {board.error}
            </p>
          )
        }
      />
      {/* Settled and empty: every read is back, none failed, and nobody is on the list. A
          sentence and the way in beat a row of lanes with nothing to drag. */}
      {!board.loading && !board.error && board.failedLanes.size === 0 && board.total === 0 ? (
        <TableEmptyState
          title="Nobody is on this list yet"
          description={
            (list.stages?.length ?? 0) === 0
              ? `Add a stage first, then add contacts to work them through it.`
              : `Add contacts to start working them through the stages.`
          }
          action={
            (list.stages?.length ?? 0) === 0 ? (
              <Button onClick={openAddStage}>
                <PlusIcon size={16} weight="bold" /> Add stage
              </Button>
            ) : (
              <Button onClick={openAddContacts}>
                <PlusIcon size={16} weight="bold" /> Add contacts
              </Button>
            )
          }
        />
      ) : (
        <ListBoard
          lanes={lanes}
          itemId={(contact) => contact.id ?? undefined}
          touchDrag
          onMove={(contactId, stageId) => bg(board.move(contactId, stageId))}
          renderCard={(contact, drag) => (
            <ListSlotCard
              contact={contact}
              config={cardConfig}
              taskCounts={taskCounts[contact.id!]}
              commContext={commContext[contact.id!]}
              dragging={drag.dragging}
              onDragStart={drag.onDragStart}
              onDragEnd={drag.onDragEnd}
              onOpen={() => bg(openContact(contact.id!))}
              onRemove={() => bg(board.remove(contact.id!))}
            />
          )}
          afterLanes={
            <Button variant="ghost" className="mt-1 shrink-0 self-start" onClick={openAddStage}>
              <PlusIcon size={16} weight="bold" /> Add stage
            </Button>
          }
        />
      )}
      {/* The board re-reads on close and on change: neither reaches the lanes on its own. */}
      <ContactDrawer
        openDrawer={Boolean(drawerContact)}
        contact={drawerContact}
        onClose={closeDrawer}
        onChange={(next) => {
          setDrawerContact(next)
          board.reload()
        }}
        nextContact={traversal.next}
        previousContact={traversal.prev}
        isNavigating={traversal.isNavigating}
      />
    </PageScroll>
  )
}
